/**
 * Time given to a photo to be uploaded: after that the request is aborted and the upload is verified on the server.
 */
export const UPLOAD_TIMEOUT_MS = 10000

/**
 * Delay between the first and the second (last) verification of an aborted upload.
 * It gives the server time to complete the saving of a photo already received.
 */
export const VERIFY_RETRY_DELAY_MS = 4000

/**
 * Timeout of the requests reading the photos of a check (baseline and verification).
 */
export const CHECK_PHOTOS_TIMEOUT_MS = 3000

const PROGRESS_AT_TIMEOUT = 90
const PROGRESS_MAX = 95

/**
 * Time-based progress (0-95) of an upload started at {@link startedAt}.
 *
 * Linear up to 90% in {@link UPLOAD_TIMEOUT_MS}, then it slowly approaches 95% while the upload is verified.
 * 100% is shown only when the upload is saved.
 */
export function uploadProgress(startedAt: number | undefined, now: number = Date.now()): number {
  if (startedAt === undefined) {
    return 0
  }

  const elapsed = Math.max(0, now - startedAt)

  if (elapsed <= UPLOAD_TIMEOUT_MS) {
    return (elapsed / UPLOAD_TIMEOUT_MS) * PROGRESS_AT_TIMEOUT
  }

  const extra = elapsed - UPLOAD_TIMEOUT_MS
  return PROGRESS_AT_TIMEOUT + (PROGRESS_MAX - PROGRESS_AT_TIMEOUT) * (1 - Math.exp(-extra / VERIFY_RETRY_DELAY_MS))
}

export interface UploadQueue {
  /**
   * Run {@link task} after the tasks already queued. The task receives a signal aborted by {@link abortCurrent}.
   */
  enqueue: (task: (signal: AbortSignal) => Promise<void>) => void
  /**
   * Abort the task in progress, if any; the queued ones are run anyway.
   */
  abortCurrent: () => void
  /**
   * A number never returned before by this queue, to build unique ids.
   */
  nextId: () => number
}

/**
 * A queue running one task at a time, in order.
 */
export function createUploadQueue(): UploadQueue {
  let tail: Promise<void> = Promise.resolve()
  let current: AbortController | null = null
  let counter = 0

  return {
    enqueue: task => {
      tail = tail.then(async () => {
        const controller = new AbortController()
        current = controller

        try {
          await task(controller.signal)
        } catch (error) {
          // a failed task must not stop the following ones
        } finally {
          current = null
        }
      })
    },
    abortCurrent: () => current?.abort(),
    nextId: () => ++counter,
  }
}
